import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  PointManagerItem,
  useGetPointManagerByUserQuery,
} from "api/baseAPI/pointManagers";
import { useGetUsersSelfQuery } from "api/baseAPI/user";
import { Layout } from "components/layouts/Layout";
import { LinearProgress } from "components/LinearProgress";
import { useSelectedPointManager } from "hooks/useSelectedEntity";
import {
  setSelectedPointManager,
  setSelectedWallet,
} from "reducers/basicSlice";

const hasReportsAccess = (pm: PointManagerItem): boolean =>
  pm.available_components?.some(
    (c) => c.name?.toLocaleLowerCase() === "reports"
  ) ?? false;

export const ReportsDownload = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const downloadUuid = searchParams.get("download_uuid");
  const accountId = searchParams.get("account_id");

  const selectedPointManager = useSelectedPointManager();

  const { data: selfData, isSuccess: selfSuccess } = useGetUsersSelfQuery();

  const { data: pointManagersData, isSuccess: pmSuccess } =
    useGetPointManagerByUserQuery(
      { user: selfData?.id || 0, page_size: "100" },
      { skip: !selfData?.id }
    );

  useEffect(() => {
    if (!downloadUuid && !accountId) navigate("/");
  }, [downloadUuid, accountId]);

  useEffect(() => {
    if (!selfSuccess || !pmSuccess) return;

    const pointManagers = pointManagersData?.results || [];
    const reportsParams = `?download_uuid=${downloadUuid}&account_id=${accountId}`;

    const currentPm = pointManagers.find(
      (pm) => pm.id === selectedPointManager?.id
    );

    if (currentPm && hasReportsAccess(currentPm)) {
      navigate(`/reports${reportsParams}`);
      return;
    }

    const pmWithAccess = pointManagers.find(
      (pm) => pm.id !== selectedPointManager?.id && hasReportsAccess(pm)
    );

    if (pmWithAccess) {
      dispatch(setSelectedPointManager({ ...pmWithAccess }));
      dispatch(
        setSelectedWallet({
          ...(pmWithAccess.main_wallet || pmWithAccess.available_wallets?.[0]),
        })
      );
      navigate(`/reports${reportsParams}`);
      return;
    }

    toast.error(t("Ошибка загрузки отчета"));
    navigate("/");
  }, [selfSuccess, pmSuccess, pointManagersData, downloadUuid, accountId]);

  return (
    <Layout title={t("Отчеты")}>
      <LinearProgress />
    </Layout>
  );
};
